import React from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { ActiveMarketEffects } from './shared/ActiveMarketEffects';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface MarketOverviewProps {
  companyState: {
    marketShare: number;
    reputation: number;
  };
  activeModifiers?: Map<string, {
    field: string;
    type: 'multiplier' | 'additive' | 'override';
    value: number;
    expiresAt?: Date;
  }>;
}

export function MarketOverview({ companyState, activeModifiers }: MarketOverviewProps) {
  // Helper functions from EnhancedEventLog for formatting modifiers
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatModifierEffect = (modifier: {
    field: string;
    type: 'multiplier' | 'additive' | 'override';
    value: number;
    expiresAt?: Date;
  }) => {
    const field = modifier.field.replace(/([A-Z])/g, ' $1').toLowerCase();
    let effectText = '';
    
    switch (modifier.type) {
      case 'multiplier': {
        const percentage = ((modifier.value - 1) * 100).toFixed(0);
        effectText = `${field}: ${modifier.value > 1 ? '+' : ''}${percentage}%`;
        break;
      }
      case 'additive':
        effectText = `${field}: ${modifier.value > 0 ? '+' : ''}${modifier.value}`;
        break;
      case 'override':
        effectText = `${field}: ${modifier.value}`;
        break;
    }
    
    return effectText;
  };

  // Convert activeModifiers to the format expected by ActiveMarketEffects
  const marketEffects = activeModifiers ? Array.from(activeModifiers.values()).map(modifier => ({
    name: formatModifierEffect(modifier).split(':')[0],
    value: formatModifierEffect(modifier).split(':')[1]?.trim() || '',
    expiration: modifier.expiresAt ? `Until ${formatDate(modifier.expiresAt)}` : ''
  })) : undefined;

  // Market growth calculation based on reputation and market share
  const growthRate = Math.min(15, Math.max(0, (companyState.reputation / 100) * (1 - companyState.marketShare) * 10));

  // Market trend data
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const marketData = months.map((_, index) => ({
    marketShare: companyState.marketShare * (1 + (index * 0.05)),
    growth: growthRate * (1 + (Math.random() * 0.2 - 0.1)),
    competition: 85 - (index * 2)
  }));

  const chartData = {
    labels: months,
    datasets: [
      {
        label: 'Market Share',
        data: marketData.map(d => d.marketShare * 100),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        tension: 0.4
      },
      {
        label: 'Growth Rate',
        data: marketData.map(d => d.growth),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.5)',
        tension: 0.4
      },
      {
        label: 'Competition Index',
        data: marketData.map(d => d.competition),
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.5)',
        tension: 0.4
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-theme-bg-content rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold mb-4 text-theme-text-primary">Market Trends</h3>
          <div className="h-64">
            <Line data={chartData} options={chartOptions} />
          </div>
        </div>

        <div className="bg-theme-bg-content rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold mb-4 text-theme-text-primary">Market Opportunities</h3>
          <div className="space-y-4">
            {[
              {
                name: 'Premium Segment',
                description: 'High-end market shows growth potential',
                roi: 25,
                risk: 'Medium'
              },
              {
                name: 'Emerging Markets',
                description: 'Expanding market in developing regions',
                roi: 35,
                risk: 'High'
              },
              {
                name: 'Innovation Focus',
                description: 'Market gap for cutting-edge AI devices',
                roi: 40,
                risk: 'High'
              }
            ].map((opportunity, index) => (
              <div key={index} className="p-4 bg-theme-bg-secondary rounded-lg">
                <h4 className="font-medium text-theme-success">
                  {opportunity.name}
                </h4>
                <p className="text-sm text-theme-text-secondary mb-3">
                  {opportunity.description}
                </p>
                <div className="flex justify-between text-sm text-theme-text-muted">
                  <span>ROI: {opportunity.roi}%</span>
                  <span>Risk: {opportunity.risk}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Active Market Effects */}
      <ActiveMarketEffects effects={marketEffects} />
    </>
  );
}